import { useState } from "react";

import { ScopeToggle } from "@/components/analytics/ScopeToggle";

// Only accessible in dev — guarded in App.tsx with import.meta.env.DEV

interface ScenarioConfig {
  label: string;
  description: string;
  // Single-value (production path)
  objectiveBucket?: string | null;
  optimizationGoalLabel?: string | null;
  // Multi-value (stress test path)
  objectiveBuckets?: string[];
  optimizationGoals?: string[];
  containerWidth?: string;
  // Pre-seed peer filters to show active badge state
  initialStartDate?: string;
  initialEndDate?: string;
  initialPeerMediaType?: "video" | "image";
}

function Scenario({
  label,
  description,
  objectiveBucket = null,
  optimizationGoalLabel = null,
  objectiveBuckets,
  optimizationGoals,
  containerWidth = "100%",
  initialStartDate,
  initialEndDate,
  initialPeerMediaType,
}: ScenarioConfig) {
  const [metricScope, setMetricScope] = useState<"all" | "bucket" | "goal">("bucket");
  const [projectScope, setProjectScope] = useState<"project" | "team">("project");
  const [startDate, setStartDate] = useState<string | undefined>(initialStartDate);
  const [endDate, setEndDate] = useState<string | undefined>(initialEndDate);
  const [peerMediaType, setPeerMediaType] = useState<"video" | "image" | undefined>(initialPeerMediaType);

  // Derive availableMetricScopes from what's provided
  const hasBuckets = (objectiveBuckets?.length ?? 0) > 0 || !!objectiveBucket;
  const hasGoals = (optimizationGoals?.length ?? 0) > 0 || !!optimizationGoalLabel;
  const availableMetricScopes: ("all" | "bucket" | "goal")[] = [
    "all",
    ...(hasBuckets ? (["bucket"] as const) : []),
    ...(hasGoals ? (["goal"] as const) : []),
  ];

  return (
    <div className="mb-10">
      <div className="mb-2">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        <span className="ml-2 text-xs text-gray-400">{description}</span>
        {containerWidth !== "100%" && (
          <span className="ml-2 text-[11px] font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
            {containerWidth}
          </span>
        )}
      </div>
      <div
        style={{ width: containerWidth, minWidth: 0 }}
        className="bg-surface border border-gray-200 rounded-xl p-4 overflow-hidden"
      >
        <ScopeToggle
          availableMetricScopes={availableMetricScopes}
          metricScope={metricScope}
          projectScope={projectScope}
          onMetricScopeChange={setMetricScope}
          onProjectScopeChange={setProjectScope}
          objectiveBucket={objectiveBucket}
          optimizationGoalLabel={optimizationGoalLabel}
          objectiveBuckets={objectiveBuckets}
          optimizationGoals={optimizationGoals}
          sampleSize={42}
          warnings={[]}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={({ startDate: s, endDate: e }) => {
            setStartDate(s);
            setEndDate(e);
          }}
          peerMediaType={peerMediaType}
          onPeerMediaTypeChange={setPeerMediaType}
        />
      </div>
    </div>
  );
}

// Compute 30d ago for the pre-seeded active state demo
const _today = new Date().toISOString().slice(0, 10);
const _30dAgo = (() => {
  const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
})();

const SCENARIOS: ScenarioConfig[] = [
  // ── UX v3: active state demos ─────────────────────────────────────────
  {
    label: "★ UX demo — inactive trigger",
    description: "No filters set — ghost 'Compare peers' button, n= has tooltip, Goal has left connector",
    objectiveBucket: "Awareness",
    optimizationGoalLabel: "Reach",
  },
  {
    label: "★ UX demo — active trigger (30d + Images)",
    description: "Blue badge visible immediately — shows active filters, × clears all in one click",
    objectiveBucket: "Awareness",
    optimizationGoalLabel: "Reach",
    initialStartDate: _30dAgo,
    initialEndDate: _today,
    initialPeerMediaType: "image",
  },

  // ── Production path (single values) ──────────────────────────────────
  {
    label: "1 · Baseline",
    description: "Production path — 2 funnel pills, 2 goal pills",
    objectiveBucket: "Awareness",
    optimizationGoalLabel: "Reach",
  },
  {
    label: "2 · Long single labels",
    description: "Worst-case production label lengths",
    objectiveBucket: "Brand Awareness Upper Funnel",
    optimizationGoalLabel: "Video ThruPlay Completion Rate",
  },

  // ── Multi-value path (stress test) ───────────────────────────────────
  {
    label: "3 · 3 funnel + 3 goals",
    description: "Small multi-option set",
    objectiveBuckets: ["Awareness", "Consideration", "Conversion"],
    optimizationGoals: ["Reach", "Link Clicks", "Purchases"],
  },
  {
    label: "4 · 5 funnel + 5 goals",
    description: "Full multi-option stress test",
    objectiveBuckets: ["Awareness", "Consideration", "Conversion", "App Promotion", "Leads"],
    optimizationGoals: ["Reach", "Impressions", "Link Clicks", "Purchases", "App Installs"],
  },
  {
    label: "5 · 5 funnel + 5 goals — long labels",
    description: "Many pills AND long text",
    objectiveBuckets: [
      "Brand Awareness",
      "Video Views",
      "Traffic & Consideration",
      "Conversion Events",
      "App Promotion",
    ],
    optimizationGoals: [
      "Reach & Frequency",
      "ThruPlay Completions",
      "Landing Page Views",
      "Purchase ROAS",
      "App Install Cost",
    ],
  },

  // ── Narrow containers ─────────────────────────────────────────────────
  {
    label: "6 · 5 funnel — 480px container",
    description: "Narrow panel + many pills",
    objectiveBuckets: ["Awareness", "Consideration", "Conversion", "App Promotion", "Leads"],
    optimizationGoals: ["Reach", "Link Clicks", "Purchases", "App Installs", "Impressions"],
    containerWidth: "480px",
  },
  {
    label: "7 · 5 funnel — 360px container",
    description: "Mobile-width panel + many pills",
    objectiveBuckets: ["Awareness", "Consideration", "Conversion", "App Promotion", "Leads"],
    optimizationGoals: ["Reach", "Link Clicks", "Purchases", "App Installs", "Impressions"],
    containerWidth: "360px",
  },
  {
    label: "8 · Long labels — 360px container",
    description: "Mobile-width + long labels + many pills",
    objectiveBuckets: [
      "Brand Awareness",
      "Video Views",
      "Traffic",
      "Conversions",
      "App Promotion",
    ],
    optimizationGoals: [
      "Reach & Frequency",
      "ThruPlay Completions",
      "Landing Page Views",
      "Purchase ROAS",
      "App Install Cost",
    ],
    containerWidth: "360px",
  },
];

export function ScopeToggleTestPage() {
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-gray-900">ScopeToggle — Stress Test</h1>
          <p className="mt-1 text-sm text-gray-500">
            Dev-only. ★ scenarios show UX v3 improvements (active badge, tooltips, connector).
            Scenarios 1–2 test production single-value path. Scenarios 3–8 test multi-option overflow.
            All scenarios are fully interactive.
          </p>
        </div>

        {SCENARIOS.map((s) => (
          <Scenario key={s.label} {...s} />
        ))}
      </div>
    </div>
  );
}
